/**
 * research-effects.ts — pure helpers that project an empire's researched
 * tech set onto numeric ship-stat and hyperdrive multipliers (M12.2).
 *
 * Every effect here is derived at read time — no mutations, no storage on
 * the empire. Callers look up the appropriate "effective" value right
 * before they need it (e.g. combat reads `effectiveArmor` at each hit
 * resolution; the jump command reads `hyperdriveCostMultiplier` once per
 * fuel deduction). This mirrors how `resolveSlotWeapon` already picks up
 * higher Mks on every fire without needing ship-state rewrites.
 *
 * Mk 1 is the implicit baseline across every ladder: a starter empire
 * with an empty tech set still behaves as if Mk 1 were researched,
 * matching ship-template stats verbatim. Higher Mks stack on top per the
 * MECHANICS §1.1 / §3 / §2.1 lock numbers.
 *
 * Deliberately NOT in scope for this milestone (M12.2):
 *   - Retroactive scaling of already-spawned ships. Newly-built ships get
 *     the boosted stats; old ships keep their original currentHull. This
 *     is a deviation from the MECHANICS "applies to every ship on research
 *     completion" wording, documented in the M12.2 session log.
 *   - Sublight Speed, non-combat techs.
 *
 * v3M3 (2026-04-28) added Shield regen via `shieldRegenRate` and the
 * `advance-shield-regen.ts` per-tick pass.
 *
 * v3M4 (2026-04-28) added EMP Protection via `empProtectionMk` /
 * `effectiveEmpProtection` and the EMP fire path in `advance-combat.ts`.
 */

import { maxHullForLevel } from './balance-structures';
import type { Empire, GameState, ShipTemplate, StructureKind } from '../state/types/index';

/**
 * Highest Mk researched in the named tech category, or 1 if the empire
 * has no techs in that category yet (Mk 1 is the always-available
 * baseline). Iterates the empire's researchedTechs Set once; small
 * enough N that a Map index isn't worth maintaining.
 */
function highestMkForCategory(
  state: GameState,
  empire: Empire,
  category: string,
): number {
  let best = 0;
  for (const techName of empire.researchedTechs) {
    const tech = state.techs.get(techName);
    if (!tech || tech.category !== category) continue;
    if (tech.level !== null && tech.level > best) best = tech.level;
  }
  return Math.max(1, best);
}

/**
 * MFix13.1b/.1c — sub-category variant. v4M5.1 collapsed Hull / Shield
 * / Armor into the `'Survivability'` category in the UI tech tree, but
 * runtime stat-application still needs per-family discrimination. The
 * `Sub-Category` column in `docs/Tech_Tree.xlsx` carries
 * `'Hull' | 'Shield' | 'Armor'` for Survivability rows and `null`
 * elsewhere; we route the three legacy ladders through this helper
 * while leaving the other categories on `highestMkForCategory`.
 */
function highestMkForSubCategory(
  state: GameState,
  empire: Empire,
  subCategory: string,
): number {
  let best = 0;
  for (const techName of empire.researchedTechs) {
    const tech = state.techs.get(techName);
    if (!tech || tech.subCategory !== subCategory) continue;
    if (tech.level !== null && tech.level > best) best = tech.level;
  }
  return Math.max(1, best);
}

// ---------------------------------------------------------------------------
// Per-ladder Mk accessors
// ---------------------------------------------------------------------------

/** Highest Hull Mk (1..6). MFix13.1b — sub-category lookup. */
export function hullMk(state: GameState, empire: Empire): number {
  return highestMkForSubCategory(state, empire, 'Hull');
}
/** Highest Armor Mk (1..6). MFix13.1b — sub-category lookup. */
export function armorMk(state: GameState, empire: Empire): number {
  return highestMkForSubCategory(state, empire, 'Armor');
}
/** Highest Shield Mk (1..6). MFix13.1b — sub-category lookup. */
export function shieldMk(state: GameState, empire: Empire): number {
  return highestMkForSubCategory(state, empire, 'Shield');
}
/**
 * Highest Hyperdrive Efficiency Mk (1..6) researched. Returns 1 (Mk-1
 * baseline) when no Hyperdrive Efficiency tech is researched — mirrors
 * the `Math.max(1, best)` floor the category-based `highestMkForCategory`
 * helper applies.
 *
 * v4M5.1 — switched from category lookup ('Hyperdrive Fuel Consumption')
 * to name-prefix lookup so the unified Engines category at v4M5.1 still
 * disambiguates Efficiency from Range / Speed.
 */
export function hyperdriveMk(state: GameState, empire: Empire): number {
  let best = 0;
  for (const techName of empire.researchedTechs) {
    if (!techName.startsWith('Hyperdrive Efficiency')) continue;
    const tech = state.techs.get(techName);
    if (!tech || tech.level === null) continue;
    if (tech.level > best) best = tech.level;
  }
  return Math.max(1, best);
}
/** Highest Electromagnetic Protection Mk (1..6) — drives EMP disable reduction (v3M4). */
export function empProtectionMk(state: GameState, empire: Empire): number {
  return highestMkForCategory(state, empire, 'Electromagnetic Protection');
}

// ---------------------------------------------------------------------------
// Effective-stat derivations
// ---------------------------------------------------------------------------

/**
 * MFix7.2 — generic accessor: walk the empire's researched techs in
 * `category`, return the parsed numeric of the highest-Mk tech, or
 * `fallback` when no researched tech in that category carries a parsed
 * value. The convert-xlsx parser populates `parsedMultiplier` /
 * `parsedAdditive` / `parsedRegenPercent` from each tech's effect
 * string at load time; this helper picks the right field per call site.
 */
export function highestParsedValue(
  state: GameState,
  empire: Empire,
  category: string,
  field: 'parsedMultiplier' | 'parsedAdditive' | 'parsedRegenPercent',
  fallback: number,
): number {
  let bestMk = 0;
  let bestVal = fallback;
  for (const techName of empire.researchedTechs) {
    const tech = state.techs.get(techName);
    if (!tech || tech.category !== category) continue;
    if (tech.level === null) continue;
    const v = tech[field];
    if (v === null) continue;
    if (tech.level > bestMk) {
      bestMk = tech.level;
      bestVal = v;
    }
  }
  return bestVal;
}

/**
 * MFix13.1b — sub-category variant of `highestParsedValue`. v4M5.1
 * collapsed Hull / Shield / Armor under the single `'Survivability'`
 * category, but the three ladders still need independent
 * multiplier / additive / regen lookups. The `Sub-Category` xlsx
 * column carries `'Hull' | 'Shield' | 'Armor'` on Survivability rows;
 * this helper filters by that field instead of `category`. Same
 * highest-Mk-wins semantics as the parent helper.
 */
export function highestParsedValueBySubCategory(
  state: GameState,
  empire: Empire,
  subCategory: string,
  field: 'parsedMultiplier' | 'parsedAdditive' | 'parsedRegenPercent',
  fallback: number,
): number {
  let bestMk = 0;
  let bestVal = fallback;
  for (const techName of empire.researchedTechs) {
    const tech = state.techs.get(techName);
    if (!tech || tech.subCategory !== subCategory) continue;
    if (tech.level === null) continue;
    const v = tech[field];
    if (v === null) continue;
    if (tech.level > bestMk) {
      bestMk = tech.level;
      bestVal = v;
    }
  }
  return bestVal;
}

/**
 * Effective max Hull for a chassis owned by `empire`. Reads the Hull
 * multiplier from the highest-Mk Hull tech the empire has researched
 * (parsed at xlsx load — MFix7.2). v3M2.2 chassisLevel scaling for
 * ODP ring platforms still applies multiplicatively.
 */
export function effectiveMaxHull(
  state: GameState,
  empire: Empire,
  tmpl: ShipTemplate,
  ship?: { chassisLevel?: number },
): number {
  const multiplier = highestParsedValueBySubCategory(state, empire, 'Hull', 'parsedMultiplier', 1.0);
  if (ship?.chassisLevel !== undefined && ship.chassisLevel > 1) {
    const chassisMult = Math.min(
      2.0,
      1.0 + (ship.chassisLevel - 1) * 0.2,
    );
    return tmpl.hull * multiplier * chassisMult;
  }
  return tmpl.hull * multiplier;
}

/**
 * Effective max Hull for a planet-side structure owned by `empire`.
 * Mirrors `effectiveMaxHull` (ships): reads the Hull multiplier from
 * the highest-Mk Hull tech the empire has researched and applies it
 * to the xlsx `hullByLevel[level - 1]` ladder.
 *
 * Used by advance-structures.ts to refresh `structure.maxHull` each
 * tick before the auto-repair pass and to set the new ceiling at
 * `promoteLevel` time. Hull tech researched mid-game lifts every
 * existing structure's max on the next tick — matching the ship
 * pattern where Hull tech grows max immediately rather than at next
 * spawn (added at the same checkpoint as MFix13.1b for ships).
 *
 * Returns 0 if the kind/level isn't in the xlsx; callers fall back
 * defensively (`structure.maxHull` stays at its stored value) when
 * this returns 0.
 */
export function effectiveStructureMaxHull(
  state: GameState,
  empire: Empire,
  kind: StructureKind,
  level: number,
): number {
  const base = maxHullForLevel(state.structuresData, kind, level);
  if (base <= 0) return 0;
  const multiplier = highestParsedValueBySubCategory(state, empire, 'Hull', 'parsedMultiplier', 1.0);
  return base * multiplier;
}

/**
 * Effective Armor fraction for a chassis owned by `empire`. Reads the
 * additive Armor bonus (% damage reduction → 0..1 fraction) from the
 * highest-Mk Armor tech the empire has researched (parsed at xlsx
 * load — MFix7.2), summed onto the chassis baseline. Capped at 0.6
 * per MECHANICS §1.1 design lock.
 *
 * Used in the combat pass: damage chain reads target's effective armor
 * on each hit, so EVERY ship of the empire benefits immediately on
 * Armor research completion (unlike Hull which is captured at spawn).
 */
export function effectiveArmor(
  state: GameState,
  empire: Empire,
  tmpl: ShipTemplate,
): number {
  const additive = highestParsedValueBySubCategory(state, empire, 'Armor', 'parsedAdditive', 0);
  return Math.min(0.6, tmpl.armor + additive);
}

/**
 * Effective max Shields for a chassis owned by `empire`. Reads the
 * Shield max-shield multiplier from the highest-Mk Shield tech
 * researched (parsed at xlsx load — MFix7.2). Mk 1 is multiplier ×1.0
 * (chassis baseline); higher Mks scale the cap directly.
 */
export function effectiveMaxShields(
  state: GameState,
  empire: Empire,
  tmpl: ShipTemplate,
): number {
  const multiplier = highestParsedValueBySubCategory(state, empire, 'Shield', 'parsedMultiplier', 1.0);
  return tmpl.shields * multiplier;
}

/**
 * Per-tick shield regen rate as a fraction of {@link effectiveMaxShields}.
 * Reads the regen rate (parsed as a 0..1 fraction; e.g. `0.001` for
 * 0.1%/tick) from the highest-Mk Shield tech researched (MFix7.2).
 * Defaults to 0.001 (Mk 1 baseline of 0.1%/tick per tech_tree.xlsx)
 * when no Shield tech carries a parsed value.
 *
 * Used by `advance-shield-regen.ts` per tick: each ship's
 * `currentShields` increments by `effectiveMaxShields × shieldRegenRate`
 * (clamped at the cap). Regen runs unconditionally for live, in-system,
 * non-EMP ships — cloak does NOT pause regen and firing does NOT pause
 * regen (v3M3 design lock 2026-04-28).
 */
export function shieldRegenRate(state: GameState, empire: Empire): number {
  return highestParsedValueBySubCategory(state, empire, 'Shield', 'parsedRegenPercent', 0.001);
}

/**
 * v4M5.1 — generic accessor for hyperdrive techs by name prefix. The
 * Engines category at v4M5.1 unifies three independent research families
 * ('Hyperdrive Efficiency', 'Hyperdrive Range', 'Hyperdrive Speed') so
 * a category-only lookup can't distinguish them. This helper filters
 * by tech-name prefix instead, walks the empire's researched techs,
 * and returns the parsed numeric of the highest-Mk match.
 *
 * Pre-v4M5.1 builds used `highestParsedValue(category=...)` directly;
 * the legacy `'Hyperdrive Fuel Consumption'` category continues to
 * parse correctly (`parseTechEffect` keeps both case-labels) so a
 * pre-v4M5.1 tech-tree.json still resolves through this helper via
 * the name-prefix path.
 */
export function highestParsedValueByNamePrefix(
  state: GameState,
  empire: Empire,
  namePrefix: string,
  field: 'parsedMultiplier' | 'parsedAdditive' | 'parsedRegenPercent',
  fallback: number,
): number {
  let bestMk = 0;
  let bestVal = fallback;
  for (const techName of empire.researchedTechs) {
    if (!techName.startsWith(namePrefix)) continue;
    const tech = state.techs.get(techName);
    if (!tech) continue;
    if (tech.level === null) continue;
    const v = tech[field];
    if (v === null) continue;
    if (tech.level > bestMk) {
      bestMk = tech.level;
      bestVal = v;
    }
  }
  return bestVal;
}

/**
 * Per-ship hyperdrive fuel cost multiplier for this empire. Reads the
 * multiplier from the highest-Mk Hyperdrive Efficiency tech researched.
 * Mk 1 = ×1.00 baseline; Mk 6 = ×0.50 (per `docs/HYPERSPACE.md §2`).
 *
 * v4M5.1 — switched from category lookup ('Hyperdrive Fuel Consumption')
 * to name-prefix lookup ('Hyperdrive Efficiency') so the unified Engines
 * category at v4M5.1 doesn't lose its Speed / Range / Efficiency
 * disambiguation. Pre-v4M5.1 saves still work — the prefix matches
 * the same tech-name format used in the legacy xlsx.
 */
export function hyperdriveCostMultiplier(
  state: GameState,
  empire: Empire,
): number {
  return highestParsedValueByNamePrefix(
    state,
    empire,
    'Hyperdrive Efficiency',
    'parsedMultiplier',
    1.0,
  );
}

/**
 * v4M5.1 — per-empire Hyperdrive Range multiplier. Reads the highest-Mk
 * Hyperdrive Range tech researched. Mk 1 = ×1.00 baseline; Mk 6 = ×3.50
 * (per `docs/HYPERSPACE.md §2.1`). Applied to each owned ship's
 * `hyperdriveRange` to derive the effective single-jump range cap.
 *
 * Not consumed at v4M5.1 — the jump pipeline rewrite at v4M5.2 wires
 * this into the order-issue range check. Surfaced now so the data layer
 * lands as a single commit.
 */
export function hyperdriveRangeMultiplier(
  state: GameState,
  empire: Empire,
): number {
  return highestParsedValueByNamePrefix(
    state,
    empire,
    'Hyperdrive Range',
    'parsedMultiplier',
    1.0,
  );
}

/**
 * v4M5.1 — per-empire Hyperdrive Speed multiplier. Reads the highest-Mk
 * Hyperdrive Speed tech researched. Mk 1 = ×1.00 baseline; Mk 6 = ×2.00
 * (per `docs/HYPERSPACE.md §2.2`). Applied to each owned ship's
 * `hyperdriveSpeed` to derive the effective travel-time-per-unit-distance.
 *
 * Not consumed at v4M5.1 — the jump pipeline rewrite at v4M5.2 wires
 * this into `travelTime = pathDistance / (template.hyperdriveSpeed × empireMul)`.
 */
export function hyperdriveSpeedMultiplier(
  state: GameState,
  empire: Empire,
): number {
  return highestParsedValueByNamePrefix(
    state,
    empire,
    'Hyperdrive Speed',
    'parsedMultiplier',
    1.0,
  );
}

/**
 * MFix7.2 — Sublight Speed multiplier for this empire (v3M6 closes
 * here). Reads the highest-Mk Sublight Engines tech's parsed
 * multiplier; default 1.0. Combined with the chassis template
 * `sublightSpeed` and `state.balanceData.movement.sublightSpeedScale`
 * at the movement step.
 *
 * MFix14 audit — switched from category lookup (`'Ship Sublight Speed'`,
 * which no longer exists post v4M5.1) to name-prefix lookup on
 * `'Sublight Engines'`. The Engines category at v4M5.1 unifies four
 * families (Sublight / Hyperdrive Speed / Hyperdrive Range /
 * Hyperdrive Efficiency); name-prefix disambiguates without an xlsx
 * schema change. Same pattern as the v4M5.1 hyperdrive accessors.
 */
export function sublightSpeedMultiplier(
  state: GameState,
  empire: Empire,
): number {
  return highestParsedValueByNamePrefix(
    state,
    empire,
    'Sublight Engines',
    'parsedMultiplier',
    1.0,
  );
}

/**
 * MFix7.2 — empire-wide additive Stealth bonus from Stealth Mk
 * research. Added on top of every ship's `stealthBase` and every
 * planet's stealth contribution. Default 0.
 *
 * MFix14 audit — category renamed from `'Non-combat: Stealth'` to
 * `'Stealth'` in the xlsx; runtime accessor updated to match.
 */
export function stealthEmpireBonus(state: GameState, empire: Empire): number {
  return highestParsedValue(state, empire, 'Stealth', 'parsedAdditive', 0);
}

/**
 * MFix7.2 — empire-wide additive Sensors bonus from Sensors Mk
 * research. Added on top of every ship's `detectionBase` and every
 * planet's detection contribution. Default 0.
 *
 * MFix14 audit — category renamed from `'Non-combat: Sensors'` to
 * `'Sensors'` in the xlsx; runtime accessor updated to match.
 */
export function sensorsEmpireBonus(state: GameState, empire: Empire): number {
  return highestParsedValue(state, empire, 'Sensors', 'parsedAdditive', 0);
}

/**
 * MFix8.3 — empire-wide multiplier on scan range from Sensors Mk
 * research. Applied to every ship's effective `scanRange` and every
 * owned planet's `scanRange` (after the LRSA additive boost). Default
 * 1.0 (no scaling) when no parsed value is present. Mirrors the dual
 * Shield pattern (max-shield multiplier + regen rate) — Sensors now
 * carries both an additive detection bonus and a multiplicative scan
 * range bonus per Mk.
 *
 * MFix14 audit — category renamed from `'Non-combat: Sensors'`.
 */
export function sensorsScanRangeMultiplier(
  state: GameState,
  empire: Empire,
): number {
  return highestParsedValue(state, empire, 'Sensors', 'parsedMultiplier', 1.0);
}
